import React, { PropsWithChildren } from 'react';
import SelectableCard, {
  SelectableCardContext,
  SelectableCardProps,
  useSelectableCardContext,
} from '../SelectableCard';
import { DropdownAnchor } from '../../Dropdown/DropdownAnchor';
import { EggSharing } from '../../Sharing/ShareTargets';
import { Egg, eggImages } from 'contexts/EggContext';
import { getLevelAndProgress } from 'components/Egg/eggUtils';
import { useEggStageContext } from 'contexts/EggStageContext';
import { CollectionEgg } from '../../Collection/model';
import { WarningIcon } from 'components/Icons/Icons';

export function SelectableEggCardDashboard(props: SelectableCardProps<Egg>) {
  return (
    <SelectableCard {...props}>
      <EggImage>
        <SelectableCard.CornerImage alt="Breaker image" />
      </EggImage>
      <SelectableCard.Body>
        <SelectableCard.Title />
        <SelectableCard.Badge outline={true} />
      </SelectableCard.Body>
      <EggProgress />
    </SelectableCard>
  );
}

/// Transformers are loosely coupled to the component that they're associated with.
/// They're a shortcut to turning a base type into the expected props for that specific card.
/// It is not necessarily required to use the base type so it lives outside the component

SelectableEggCardDashboard.transformer = (egg: Egg): SelectableCardContext<Egg> => {
  const { level } = getLevelAndProgress(egg.points);
  return {
    title: `Egg #${egg.id}`,
    badge: level,
    ctx: egg,
  };
};

export function SelectableEggCardProfile(props: SelectableCardProps<Egg>) {
  return (
    <SelectableCard {...props}>
      <EggImage />
      <SelectableCard.Body>
        <div>
          <SelectableCard.Title />
          <SelectableCard.Subtitle />
        </div>
        <DropdownAnchor>
          <EggSharing egg={{ id: props.card.ctx.id }} />
        </DropdownAnchor>
      </SelectableCard.Body>
      <EggProgress />
    </SelectableCard>
  );
}

export function SelectableEggCardHatchening(props: SelectableCardProps<Egg>) {
  return (
    <SelectableCard {...props}>
      <EggImage>
        <SelectableCard.CornerImage alt="Breaker image" />
      </EggImage>
      <SelectableCard.Body>
        <div>
          <SelectableCard.Subtitle />
          <SelectableCard.Title />
        </div>
      </SelectableCard.Body>
    </SelectableCard>
  );
}

SelectableEggCardProfile.transformer = (egg: Egg): SelectableCardContext<Egg> => {
  const { level } = getLevelAndProgress(egg.points);
  return {
    title: `Egg #${egg.id}`,
    subtitle: `Level ${level}`,
    ctx: egg,
  };
};

export function SelectableEggCardCollection(props: SelectableCardProps<unknown>) {
  return (
    <SelectableCard {...props}>
      <SelectableCard.Image alt="Gribble egg">
        <SelectableCard.CornerImage alt="Paired Breaker" />
      </SelectableCard.Image>
      <SelectableCard.Body>
        <div>
          <SelectableCard.Subtitle />
          <SelectableCard.Title />
        </div>
        <SelectableCard.Badge outline={false} />
      </SelectableCard.Body>
    </SelectableCard>
  );
}

SelectableEggCardCollection.transformer = (
  egg: CollectionEgg,
): SelectableCardContext<CollectionEgg> => {
  return {
    subtitle: `Level ${egg.level.toString()}`,
    title: `Gribble Egg #${egg.id}`,
    badge: egg.hatching ? <WarningIcon /> : undefined,
    cornerImage: egg.pairedBreaker?.breakerSrc,
    image: egg.image.thumbnail,
    ctx: egg,
  };
};

function EggProgress() {
  return (
    <SelectableCard.Progress<Egg>
      selector={(card) => {
        const { progress } = getLevelAndProgress(card.ctx.points);
        return progress;
      }}
    />
  );
}

// using a slight hack here because the value of the image
// depends on the current stage which is in a react context
function EggImage({ children }: PropsWithChildren<{}>) {
  const { card } = useSelectableCardContext() as SelectableCardProps<CollectionEgg | Egg>;
  const stage = useEggStageContext();
  const derivedImage =
    'level' in card.ctx
      ? eggImages.static(card.ctx.level, stage)
      : eggImages.staticFromPoints(card.ctx.points, stage);
  return (
    <SelectableCard.Image src={derivedImage} alt="Egg">
      {children}
    </SelectableCard.Image>
  );
}
